// @flow
import React, { Component, Fragment } from "react";
import classNames from "classnames";
import isEqual from "lodash/isEqual";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import { withStyles } from "@material-ui/core/styles";

import type { DictionaryType } from "../../types/GeneralTypes";
import type { StyledTextFieldClassesType } from "./StyledTextField";
import SingleCheckBox from "./SingleCheckBox";
import StyledTextField, { InputStyles } from "./StyledTextField";
import TextReplacableInput from "./TextReplacableInput";

const styles = (theme: DictionaryType): DictionaryType => ({
  ...InputStyles(theme),
  paper: {
    width: "100%"
  },
  root: {
    display: "flex"
  },
  helper: {
    verticalAlign: "middle",
    paddingLeft: "10px",
    paddingRight: "10px"
  },
  checkboxAdjust: {
    marginTop: "29px"
  }
});

type KeyValuePairsInputPropsType = {
  classes: {
    ...StyledTextFieldClassesType,
    paper: DictionaryType,
    root: DictionaryType,
    helper: DictionaryType,
    checkboxAdjust: DictionaryType
  },
  label: string,
  name: string,
  value: Array<PairType>,
  isRequired: boolean,
  isInvalid: boolean,
  syncChange: Function,
  isKeyReplacable?: boolean,
  isValueReplacable?: boolean
};

type PairType = {
  key: string,
  value: string,
  selected: boolean
};

type KeyValuePairsInputStatesType = {
  pairs: Array<PairType>
};

class KeyValuePairsInput extends Component<
  KeyValuePairsInputPropsType,
  KeyValuePairsInputStatesType
> {
  constructor(props: KeyValuePairsInputPropsType) {
    super(props);
    this.state = {
      pairs: [{ key: "", value: "", selected: false }]
    };
  }

  static getDerivedStateFromProps(
    nextProps: KeyValuePairsInputPropsType,
    prevState: KeyValuePairsInputStatesType
  ): KeyValuePairsInputStatesType {
    if (!isEqual(nextProps.value, prevState.pairs)) {
      let pairs = [...nextProps.value];
      pairs.push({ key: "", value: "", selected: false });

      return { pairs: pairs };
    } else {
      return null;
    }
  }

  handleChange = (index: number): * => (e: SyntheticInputEvent<*>): * => {
    const name = e.target.name;
    const value = e.target.value;
    let pairs = [...this.state.pairs];
    pairs[index][name] = value;

    if (name === "key" && index === this.state.pairs.length - 1) {
      pairs[index].selected = true;
      pairs.push({ key: "", value: "", selected: false });
    }

    this.setState({ pairs: pairs }, this.syncChange);
  };

  handleCheckBoxChange = (index: number): * => (
    e: SyntheticInputEvent<*>
  ): * => {
    const name = e.target.name;
    let pairs = [...this.state.pairs];
    pairs[index][name] = !pairs[index][name];
    this.setState({ pairs: pairs }, this.syncChange);
  };

  handleReplaceText = (
    keyName: string,
    value: *,
    doneCall?: Function
  ): Function => {
    const splitedList = keyName.split("-");
    const name = splitedList[1];
    const index = parseInt(splitedList[2]);

    let pairs = [...this.state.pairs];
    pairs[index][name] = value;

    if (name === "key" && index === this.state.pairs.length - 1) {
      pairs[index].selected = true;
      pairs.push({ key: "", value: "", selected: false });
    }

    this.setState({ pairs: pairs }, (): * => {
      doneCall && doneCall();
      this.syncChange();
    });
  };

  syncChange = (): * => {
    this.props.syncChange(this.props.name, this.state.pairs);
  };

  render(): React$Element<*> {
    const classes = this.props.classes;

    return (
      <Grid container spacing={8}>
        <Grid item xs={12}>
          <FormControl fullWidth={true}>
            <InputLabel
              required={this.props.isRequired}
              error={this.props.isInvalid}
              shrink={true}
              classes={{
                root: classes.greenLabel,
                formControl: classes.textFieldFormLabel,
                focused: classes.labelFocused,
                error: classes.labelError,
                disabled: classes.labelDisabled
              }}
            >
              {this.props.label}
            </InputLabel>
          </FormControl>
        </Grid>
        <Paper className={classes.paper}>
          {this.state.pairs.map((pair: PairType, i: number): * => {
            return (
              <div className={classes.root} key={i}>
                <Grid item xs={1} className={classes.checkboxAdjust}>
                  <SingleCheckBox
                    name="selected"
                    value={pair.selected}
                    onChange={this.handleCheckBoxChange(i)}
                  />
                </Grid>
                <Grid item xs={5}>
                  {this.props.isKeyReplacable ? (
                    <TextReplacableInput
                      name={this.props.name + "-key-" + i}
                      value={pair.key}
                      isRequired={false}
                      syncChange={this.handleReplaceText}
                      startAdornment={
                        <span className={classes.helper}>Key</span>
                      }
                    />
                  ) : (
                    <StyledTextField
                      name="key"
                      value={pair.key}
                      onChange={this.handleChange(i)}
                      inpuType="text"
                      startAdornment={
                        <span className={classes.helper}>Key</span>
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={5}>
                  {this.props.isValueReplacable ? (
                    <TextReplacableInput
                      name={this.props.name + "-value-" + i}
                      value={pair.value}
                      isRequired={false}
                      syncChange={this.handleReplaceText}
                      startAdornment={
                        <span className={classes.helper}>Value</span>
                      }
                    />
                  ) : (
                    <StyledTextField
                      name="value"
                      value={pair.value}
                      onChange={this.handleChange(i)}
                      inpuType="text"
                      startAdornment={
                        <span className={classes.helper}>Value</span>
                      }
                    />
                  )}
                </Grid>
                <Grid item xs={1} />
              </div>
            );
          })}
        </Paper>
      </Grid>
    );
  }
}

export default withStyles(styles)(KeyValuePairsInput);
